import React, { FC, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { toJS } from 'mobx';
import styled from '@emotion/styled';
import { Regular14TransparentBlack600, Regular18TransparentBlack900 } from '@app/components/Text';
import MorteeMode from '@mortee/morteeMode';
import validationSystemServices from '@mortee/services/validationSystemServices';
import EditValidationRecordForm from '@mortee/routes/validationSystem/editValidtionRecord/EditValidationRecordForm';
import useLoadable from '@app/hooks/loadable/useLoadable';
import {
  NULL_ORG,
  SupplierValidationRecord,
  SupplierValidationRecordStatusSubsets,
  transformSupplierValidationRecord,
} from '@mortee/domain/validationSystem';
import useInfraStores from '@app/hooks/useInfraStores';
import useAppStores from '@app/hooks/useAppStores';
import Loader from '@app/components/Loader';
import { LoadableCreator } from '@app/utils/Loadable';
import { showYesNoModalAsync } from '@app/components/Modal';
import CopyableValue from '@app/components/CopyableValue';
import { isDefined } from '@app/utils/utils';
import { MainTabs } from '@mortee/routes/validationSystem/editValidtionRecord/mainTabs/EditValidationRecordMainTabs';
import { SecondaryTabs } from '@mortee/routes/validationSystem/editValidtionRecord/secondaryTabs/EditValidationRecordSecondaryTabs';
import SeparatorLine from '@app/components/SeparatorLine';
import PageWithHeader from '@app/components/PageWithHeader';
import PageHeader from '@app/components/header/PageHeader';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import InlinePreviousAndNextArrows from '@app/components/InlinePreviousAndNextArrows';
import { ButtonProps } from '@app/components/Button';
import { EditValidationRecordNavigationContext, INITIAL_PAGE_NUMBER } from '@mortee/domain/validationRecordTable';

type EnumValue<T> = T[keyof T];

interface RouteParams {
  staticId: string;
}

const INITIAL_MAIN_TAB = MainTabs.generalInfo;

const EditValidationRecordPage: FC = observer(() => {
  const { userStore, navigationStore } = useInfraStores<MorteeMode>();
  const { validationSystemStore } = useAppStores<MorteeMode>();
  const history = useHistory();
  const location = useLocation();
  const { staticId } = useParams<RouteParams>();

  const [selectedMainTab, setSelectedMainTab] = useState<EnumValue<typeof MainTabs> | null>(null);
  const [selectedSecondaryTab, setSelectedSecondaryTab] = useState<EnumValue<typeof SecondaryTabs>>(SecondaryTabs.comments);

  const isAnyFormSavedRef = useRef<boolean>(false);
  const isCurrentFormUnsaved = useRef<boolean>(false);

  // Navigation context carried from the records table so the previous/next arrows walk the exact
  // filtered/sorted list the user was viewing. May be absent on a hard refresh / deep link.
  const navigationContext = location.state as EditValidationRecordNavigationContext | undefined;

  const indexInPage = navigationContext ? navigationContext.recordIds.indexOf(staticId ?? '') : -1;
  const atFirstOfPage = indexInPage === 0;
  const atLastOfPage = navigationContext ? indexInPage === navigationContext.recordIds.length - 1 : false;

  // When the current record sits at the first/last position of the page held in navigationContext,
  // the previous/next neighbor lives on an adjacent server page. Fetch those adjacent pages (only the
  // ones we actually need) using the SAME filters/sort/search, so crossing a page boundary lands on
  // the correct filtered record and the forwarded context stays a complete, ordered list.
  const [adjacentPagesLoadable] = useLoadable(async () => {
    if (!navigationContext || indexInPage === -1) {
      return { previousPageIds: undefined, nextPageIds: undefined };
    }

    const { combinedFilters, sort, search, page, pageSize, totalElements } = navigationContext;

    const fetchPageIds = async (targetPage: number): Promise<string[]> => {
      const response = search
        ? await validationSystemServices.searchValidationRecords(targetPage, pageSize, search)
        : await validationSystemServices.getValidationRecords(targetPage, pageSize, combinedFilters, sort);
      return response.content.map((record) => record.staticId);
    };

    const hasPreviousPage = page > INITIAL_PAGE_NUMBER;
    const hasNextPage = page * pageSize < totalElements;

    try {
      const [previousPageIds, nextPageIds] = await Promise.all([
        atFirstOfPage && hasPreviousPage ? fetchPageIds(page - 1) : Promise.resolve(undefined),
        atLastOfPage && hasNextPage ? fetchPageIds(page + 1) : Promise.resolve(undefined),
      ]);
      return { previousPageIds, nextPageIds };
    } catch {
      return { previousPageIds: undefined, nextPageIds: undefined };
    }
  }, [navigationContext, indexInPage, atFirstOfPage, atLastOfPage]);

  const previousPageIds = adjacentPagesLoadable.isResolved() ? adjacentPagesLoadable.result.previousPageIds : undefined;
  const nextPageIds = adjacentPagesLoadable.isResolved() ? adjacentPagesLoadable.result.nextPageIds : undefined;

  // Neighbor within the current page comes straight from the passed list; a neighbor on an adjacent
  // page comes from the fetched adjacent page. Without a navigation context there is no list to walk.
  const previousRecordId =
    (indexInPage > 0 ? navigationContext?.recordIds[indexInPage - 1] : undefined) ??
    (atFirstOfPage && previousPageIds?.length ? previousPageIds[previousPageIds.length - 1] : undefined);
  const nextRecordId =
    (navigationContext && indexInPage >= 0 && indexInPage < navigationContext.recordIds.length - 1
      ? navigationContext.recordIds[indexInPage + 1]
      : undefined) ?? (atLastOfPage && nextPageIds?.length ? nextPageIds[0] : undefined);

  // Build the navigation context to forward when moving to `targetStaticId`. When the target lives on
  // the current page we keep the same list; when it is on an adjacent page we shift page/recordIds to
  // that page's full list so the next hop keeps walking correctly. Undefined when we have no context.
  function buildContextForTarget(targetStaticId: string): EditValidationRecordNavigationContext | undefined {
    if (!navigationContext) {
      return undefined;
    }

    if (navigationContext.recordIds.includes(targetStaticId)) {
      return navigationContext;
    }

    if (previousPageIds?.includes(targetStaticId)) {
      return { ...navigationContext, page: navigationContext.page - 1, recordIds: previousPageIds };
    }
    if (nextPageIds?.includes(targetStaticId)) {
      return { ...navigationContext, page: navigationContext.page + 1, recordIds: nextPageIds };
    }

    return navigationContext;
  }

  const currentStaticIdRef = useRef<string>(staticId || '');
  // We need the static id as ref because the static id from the params does not update to the latest value (due to closure)
  currentStaticIdRef.current = staticId || '';

  const [recordLoadable, reloadRecord, setRecordLoadable] = useLoadable(async () => {
    if (!staticId) {
      throw new Error('Static ID is required');
    }
    const serverResponse = await validationSystemServices.getValidationRecordByStaticId(staticId);
    return transformSupplierValidationRecord(serverResponse, userStore.user?.id);
  }, [staticId]);

  async function onChangeMainTab(newTab: MainTabs): Promise<void> {
    if (await checkCanChangeTab()) {
      // We left the tab so there is no unsaved form data
      isCurrentFormUnsaved.current = false;
      setSelectedMainTab(newTab);
    }
  }

  async function checkCanChangeTab(): Promise<boolean> {
    if (isCurrentFormUnsaved.current) {
      return await showYesNoModalAsync('This tab has unsaved changes', `Leave editing tab`, 'Leave Without Saving', 'Stay');
    }

    return true;
  }

  async function checkCanNavigate(): Promise<boolean> {
    if (isCurrentFormUnsaved.current) {
      return await showYesNoModalAsync(
        'This form has unsaved changes',
        `Leave editing record ${recordLoadable.result?.presentationId}?`,
        'Leave Without Saving',
        'Stay',
      );
    }

    return true;
  }

  function navigateToRecord(targetStaticId: string): void {
    const editLink = navigationStore.generateEditValidationRecordLink(targetStaticId);
    // Forward the (possibly page-shifted) filtered list so the arrows keep walking the same view.
    // toJS() produces a plain snapshot: navigationContext is read from location.state, which the
    // observable-history wrapper exposes as a MobX Proxy. Passing that Proxy straight back into
    // history.push would make pushState's structured clone throw
    // ("DataCloneError: #<Object> could not be cloned").
    const navigationState = toJS(buildContextForTarget(targetStaticId));

    if (typeof editLink === 'string') {
      history.push(editLink, navigationState);
    } else if (editLink && editLink.pathname) {
      history.push({ ...editLink, state: navigationState });
    }
  }

  async function onPreviousRecordClick(): Promise<void> {
    if (!previousRecordId) {
      return;
    }

    navigateToRecord(previousRecordId);
  }

  async function onNextRecordClick(): Promise<void> {
    if (!nextRecordId) {
      return;
    }

    navigateToRecord(nextRecordId);
  }

  function wrapMoveClickWithQuestionBefore(moveAction: ButtonProps['onClick'] | undefined): ButtonProps['onClick'] | undefined {
    if (moveAction) {
      return async (...args) => {
        if (await checkCanNavigate()) {
          moveAction(...args);
        }
      };
    }
  }

  function onSaved(
    updatedRecordStaticId: string,
    updatedRecord: SupplierValidationRecord | undefined,
    userWantsToClose: boolean,
  ): void {
    isAnyFormSavedRef.current = true;

    if (updatedRecordStaticId !== currentStaticIdRef.current) {
      return;
    }

    if (userWantsToClose) {
      // Go back to the Validation System table. The filter the user had applied is restored there from
      // the store (ValidationSystemStore.lastTableState); the selected view (`view` query param) is
      // likewise restored from the store so the user returns to the same view they left, instead of the
      // role default.
      history.push(navigationStore.generateSupplierValidationRecordsTablePageLink(validationSystemStore.lastTableView));
      return;
    }

    if (updatedRecord) {
      setRecordLoadable(LoadableCreator.resolved(updatedRecord));
    } else {
      reloadRecord.silent();
    }
  }

  function onRecordRefreshRequested(): void {
    reloadRecord.silent();
  }

  useEffect(() => {
    if (recordLoadable.isResolved() && selectedMainTab === null) {
      setSelectedMainTab(getMainTabByRecordData(recordLoadable.result));
    }
  }, [recordLoadable, selectedMainTab]);

  function getMainTabByRecordData(record: SupplierValidationRecord): MainTabs {
    if (SupplierValidationRecordStatusSubsets.shouldOpenEditModalOnVerificationTab.includes(record.status.value)) {
      return MainTabs.verification;
    }

    return INITIAL_MAIN_TAB;
  }

  const headerContent = (
    <HeaderLayout>
      <LeftSection>
        {recordLoadable.resolve(
          (record) => (
            <CopyableValue dataTestId='presentationId-copyable' label='VR' value={record.presentationId}>
              <RecordIdText>{record.presentationId}</RecordIdText>
            </CopyableValue>
          ),
          () => (
            <RecordIdText>Loading...</RecordIdText>
          ),
        )}
      </LeftSection>
      <CenterSection>
        {recordLoadable.resolve(
          (record) => (
            <>
              <MainTitle>
                <CopyableValue dataTestId='supplierName-copyable' label='Supplier name' value={record.supplierName}>
                  {record.supplierName}
                </CopyableValue>
              </MainTitle>
              <Subtitle>
                Referring customer -{' '}
                {record.organization?.name ? (
                  <CopyableValue dataTestId='customerName-copyable' label='Customer name' value={record.organization?.name} />
                ) : (
                  NULL_ORG.name
                )}
              </Subtitle>
            </>
          ),
          () => (
            <>
              <MainTitle>Loading...</MainTitle>
              <Subtitle>Loading...</Subtitle>
            </>
          ),
        )}
      </CenterSection>

      <RightSection>
        <RightSectionContent>
          <CloseButton
            onClick={async (): Promise<void> => {
              if (await checkCanNavigate()) {
                onSaved(staticId, undefined, true);
              }
            }}
            aria-label='Close'
          >
            ✕
          </CloseButton>
          <StyledPreviousAndNextArrows
            onPreviousClick={wrapMoveClickWithQuestionBefore(previousRecordId ? onPreviousRecordClick : undefined)}
            previousItemText='Previous Record'
            onNextClick={wrapMoveClickWithQuestionBefore(nextRecordId ? onNextRecordClick : undefined)}
            nextItemText='Next Record'
            disableButtons={!recordLoadable.isResolved()}
          />
        </RightSectionContent>
      </RightSection>
    </HeaderLayout>
  );

  if (!staticId) {
    return (
      <PageWithHeader width='fullWithoutPadding' paddingTop='none' header={<PageHeader hideTitle sideContent={headerContent} />}>
        <div>Error: Static ID is required</div>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader width='fullWithoutPadding' paddingTop='none' header={<PageHeader hideTitle sideContent={headerContent} />}>
      <div data-testid='edit-validation-record-page'>
        <PageContainer>
          <SeparatorLine />
          {recordLoadable.resolve(
            (record) => (
              <ExpandedEditValidationRecordForm
                key={record.staticId}
                record={record}
                selectedMainTab={{ value: selectedMainTab ?? INITIAL_MAIN_TAB, onChange: onChangeMainTab }}
                selectedSecondaryTab={{ value: selectedSecondaryTab, onChange: setSelectedSecondaryTab }}
                onSaved={(updatedRecord, userWantsToClose): void => onSaved(staticId, updatedRecord, userWantsToClose)}
                onRecordRefreshRequested={onRecordRefreshRequested}
                onIsFormUnsavedChanged={(isFormUnsaved): void => {
                  if (isDefined(isFormUnsaved)) {
                    isCurrentFormUnsaved.current = isFormUnsaved;
                  }
                }}
              />
            ),
            () => (
              <Loader spinning transparent />
            ),
          )}
        </PageContainer>
      </div>
    </PageWithHeader>
  );
});

export default EditValidationRecordPage;

const PageContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  position: relative;
  box-shadow: var(--box-shadow-3);
  padding: 0;
  background: white;
  border-radius: 8px;

  display: flex;
  flex-direction: column;
  // Bounded height (not min-height): gives the 3 columns a fixed frame to scroll INSIDE, so the
  // Verification tab's sticky top/footer work and the whole page doesn't grow + scroll as one block.
  height: calc(100vh - 120px);
  min-height: 0;
  overflow: hidden;

  --side-padding: 24px;
`;

const ExpandedEditValidationRecordForm = styled(EditValidationRecordForm)`
  flex: 1;
  // Shares the same DOM node as MainLayout (EditValidationRecordForm renders
  // <MainLayout className={className}>). overflow:hidden (not overflow-y:auto) so it does NOT override
  // MainLayout's overflow:hidden — otherwise the whole form scrolls as one block and the footer/columns
  // never lock. Scrolling stays inside each column.
  overflow: hidden;
  min-height: 0;
`;

const HeaderLayout = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  width: 100%;
  min-height: 80px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const CenterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const RightSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: rgba(28, 32, 36, 0.6);
  padding: 4px 6px;
  border-radius: 4px;
  transition:
    color 0.15s,
    background 0.15s;
  align-self: flex-end;

  &:hover {
    color: #1c2024;
    background: rgba(28, 32, 36, 0.07);
  }
`;

const MainTitle = styled(Regular18TransparentBlack900.div)`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const Subtitle = styled(Regular14TransparentBlack600.div)`
  font-size: 14px;
  color: #666;
`;

const StyledPreviousAndNextArrows = styled(InlinePreviousAndNextArrows)`
  align-self: flex-start;
  flex: 0 0 auto;
  justify-content: flex-end;
  align-items: baseline;
`;

const RecordIdText = styled.span`
  font-size: 14px;
  color: var(--transparent-black-900);
`;
